// =============================================================
// BTR Companion — couche Data Dragon (métadonnées statiques Riot)
// Charge versions + champions + items + sorts + runes une fois,
// puis expose des lookups synchrones. L'app attend `initDdragon()`.
// =============================================================
const LANG = 'fr_FR';
const CDN = 'https://ddragon.leagueoflegends.com/cdn';

let VERSION = '14.20.1';
const champById = {};   // "Ahri"       -> { id, key, name, title, tags, partype, blurb, info }
const champByKey = {};  // 103 (number) -> idem
const itemsById = {};   // 3157 -> { id, name, gold, tags, boots }
const spellsById = {};  // 4 -> { key:"SummonerFlash", name }
const runeStyles = {};  // 8000 -> { id, key, name, icon }
const runePerks = {};   // 8112 -> { id, name, icon, styleId, keystone }
const fullCache = {};   // "Ahri" -> données complètes du champion (sorts, lore…)

let _ready = null;

async function getJSON(url) {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`ddragon ${r.status} ${url}`);
  return r.json();
}

// ---------- Cache localStorage des maps compactes ----------
// Les données ddragon sont immuables par version : on sérialise les maps
// déjà digérées (~10× plus légères que les JSON bruts) sous une clé par
// version+langue. Démarrage suivant : 1 seul fetch (versions.json, ~5 Ko)
// au lieu de ~1,5 Mo. Les vieilles clés btr-dd-* sont purgées au passage.
const CACHE_PREFIX = 'btr-dd-';
const cacheKey = (v) => `${CACHE_PREFIX}${v}-${LANG}`;

function loadFromCache(version) {
  try {
    const raw = localStorage.getItem(cacheKey(version));
    if (!raw) return false;
    const s = JSON.parse(raw);
    if (!s || !s.champById || !Object.keys(s.champById).length) return false;
    Object.assign(champById, s.champById);
    Object.assign(itemsById, s.itemsById);
    Object.assign(spellsById, s.spellsById);
    Object.assign(runeStyles, s.runeStyles);
    Object.assign(runePerks, s.runePerks);
    Object.values(champById).forEach((c) => { champByKey[c.key] = c; });
    return true;
  } catch { return false; }
}

function saveToCache(version) {
  try {
    // Purge des versions précédentes (une seule entrée conservée)
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(CACHE_PREFIX) && k !== cacheKey(version)) localStorage.removeItem(k);
    }
    localStorage.setItem(cacheKey(version), JSON.stringify({
      champById, itemsById, spellsById, runeStyles, runePerks,
    }));
  } catch { /* quota plein ou navigation privée : tant pis, on refetchera */ }
}

export function initDdragon() {
  if (_ready) return _ready;
  _ready = (async () => {
    try {
      const versions = await getJSON(`${CDN.replace('/cdn', '')}/api/versions.json`);
      if (Array.isArray(versions) && versions[0]) VERSION = versions[0];
    } catch {
      // Hors-ligne / CDN indisponible : on repart de la dernière version cachée.
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith(CACHE_PREFIX) && k.endsWith(`-${LANG}`)) {
            VERSION = k.slice(CACHE_PREFIX.length, -(LANG.length + 1));
            break;
          }
        }
      } catch { /* garde le fallback */ }
    }

    if (loadFromCache(VERSION)) return; // maps hydratées depuis localStorage

    const [champs, items, summoners, runes] = await Promise.all([
      getJSON(`${CDN}/${VERSION}/data/${LANG}/champion.json`).catch(() => ({ data: {} })),
      getJSON(`${CDN}/${VERSION}/data/${LANG}/item.json`).catch(() => ({ data: {} })),
      getJSON(`${CDN}/${VERSION}/data/${LANG}/summoner.json`).catch(() => ({ data: {} })),
      getJSON(`${CDN}/${VERSION}/data/${LANG}/runesReforged.json`).catch(() => ([])),
    ]);

    Object.values(champs.data || {}).forEach((c) => {
      const entry = {
        id: c.id, key: Number(c.key), name: c.name, title: c.title,
        tags: c.tags || [], partype: c.partype, blurb: c.blurb, info: c.info || {},
      };
      champById[c.id] = entry;
      champByKey[entry.key] = entry;
    });

    Object.entries(items.data || {}).forEach(([id, it]) => {
      itemsById[Number(id)] = {
        id: Number(id), name: it.name, gold: it.gold?.total ?? 0,
        tags: it.tags || [], boots: (it.tags || []).includes('Boots'),
      };
    });

    Object.values(summoners.data || {}).forEach((sp) => {
      spellsById[Number(sp.key)] = { key: sp.id, name: sp.name };
    });

    (Array.isArray(runes) ? runes : []).forEach((st) => {
      runeStyles[st.id] = { id: st.id, key: st.key, name: st.name, icon: st.icon };
      (st.slots || []).forEach((slot, slotIdx) => {
        (slot.runes || []).forEach((r) => {
          runePerks[r.id] = {
            id: r.id, name: r.name, icon: r.icon, styleId: st.id, keystone: slotIdx === 0,
          };
        });
      });
    });

    if (Object.keys(champById).length) saveToCache(VERSION);
  })();
  return _ready;
}

// ---------- Lookups synchrones (après init) ----------
export const ddVersion = () => VERSION;
export const getChamp = (idOrKey) =>
  (typeof idOrKey === 'number' ? champByKey[idOrKey] : champById[idOrKey]) || null;
export const allChamps = () => Object.values(champById);
export const getItem = (id) => itemsById[Number(id)] || null;
export const getSpell = (id) => spellsById[Number(id)] || null;
export const getRuneStyle = (id) => runeStyles[Number(id)] || null;
export const getRunePerk = (id) => runePerks[Number(id)] || null;

// Stat shards : absents de runesReforged.json, table fixe (ids Riot stables)
const STAT_SHARDS = {
  5001: { name: 'PV (croissance)', icon: 'perk-images/StatMods/StatModsHealthScalingIcon.png' },
  5005: { name: "Vitesse d'attaque", icon: 'perk-images/StatMods/StatModsAttackSpeedIcon.png' },
  5007: { name: 'Accélération de compétences', icon: 'perk-images/StatMods/StatModsCDRScalingIcon.png' },
  5008: { name: 'Force adaptative', icon: 'perk-images/StatMods/StatModsAdaptiveForceIcon.png' },
  5010: { name: 'Vitesse de déplacement', icon: 'perk-images/StatMods/StatModsMovementSpeedIcon.png' },
  5011: { name: 'PV', icon: 'perk-images/StatMods/StatModsHealthPlusIcon.png' },
  5013: { name: 'Ténacité', icon: 'perk-images/StatMods/StatModsTenacityIcon.png' },
};
export const getStatShard = (id) => STAT_SHARDS[Number(id)] || null;

// ---------- URLs d'images ----------
export const champSquare = (id) => `${CDN}/${VERSION}/img/champion/${id}.png`;
export const champLoading = (id, skin = 0) => `${CDN}/img/champion/loading/${id}_${skin}.jpg`;
export const champSplash = (id, skin = 0) => `${CDN}/img/champion/splash/${id}_${skin}.jpg`;
export const champCentered = (id, skin = 0) => `${CDN}/img/champion/centered/${id}_${skin}.jpg`;
export const itemImg = (id) => `${CDN}/${VERSION}/img/item/${id}.png`;
export const spellImgByKey = (key) => `${CDN}/${VERSION}/img/spell/${key}.png`;
export const runeStyleIcon = (icon) => `https://ddragon.leagueoflegends.com/cdn/img/${icon}`;
export const profileIcon = (id) => `${CDN}/${VERSION}/img/profileicon/${id}.png`;
// Image générique ddragon : imgPath('spell', 'AhriQ.png') / imgPath('passive', '...')
export const imgPath = (sub, file) => `${CDN}/${VERSION}/img/${sub}/${file}`;

// Détails complets d'un champion (sorts, lore, stats) — lazy + cache.
export async function fetchChampionFull(id) {
  if (fullCache[id]) return fullCache[id];
  const data = await getJSON(`${CDN}/${VERSION}/data/${LANG}/champion/${id}.json`);
  const full = data.data?.[id] || null;
  fullCache[id] = full;
  return full;
}
